import React, { useState, useEffect } from "react";
import { UserPen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { ALL_MENU_ITEMS } from "constants/common";
import { getDefaultMenuAccess, getMenuAccessKey } from "utils/helper";
import { updateCompanyUserApi } from "actions/userAction";
import iziToast from "izitoast";
import { useDispatch } from "react-redux";
import { clearTableData } from "../../redux/tableSlice";

const EditUserModal = ({
  isEditUserModalOpen,
  setIsEditUserModalOpen,
  editUser = {},
  onUpdateSuccess,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [userData, setUserData] = useState(() => ({
    ...editUser,
    company_menu_access: editUser?.company_menu_access ?? getDefaultMenuAccess(),
  }));

  // Sync userData when modal opens or editUser changes (e.g. user selects a different row)
  useEffect(() => {
    if (isEditUserModalOpen && editUser?.id) {
      setUserData({
        ...editUser,
        company_menu_access: editUser?.company_menu_access ?? getDefaultMenuAccess(),
      });
    }
  }, [isEditUserModalOpen, editUser?.id]);

  const handleMenuAccessChange = (key, view) => {
    setUserData((prev) => ({
      ...prev,
      company_menu_access: {
        ...(prev.company_menu_access ?? {}),
        [key]: { ...(prev.company_menu_access?.[key] ?? {}), view },
      },
    }));
  };
  const handleCancel = () => {
    setIsEditUserModalOpen(false);
  };

  const handleInputChange = (field, value) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateUser = async (e) => {
    e?.preventDefault();
    console.log("userData====>", userData);
    // TODO: call API to update user with userData

    const payload = {
      name: userData.name,
      email: userData.email,
      company_menu_access: userData.company_menu_access,
    };

    setIsLoading(true);
    const res = await updateCompanyUserApi({ id: userData?.id, payload });
    console.log("Update Company User", { res });
    if (res.status) {
      iziToast.success({
        title: "Success",
        message: res.message,
        position: "topCenter",
      });
      dispatch(clearTableData());
      onUpdateSuccess && onUpdateSuccess();
    }

    setIsLoading(false);
    setIsEditUserModalOpen(false);
  };

  return (
    <Dialog
      open={isEditUserModalOpen}
      onOpenChange={setIsEditUserModalOpen}
      className="bg-black/20"
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-[760px] sm:w-full p-0 gap-0 overflow-hidden rounded-xl border border-gray-200/80 shadow-xl bg-white">
        <form onSubmit={handleUpdateUser}>
          <DialogHeader className="relative px-6 pt-6 pb-5 border-b border-gray-200/80 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-s360-violet rounded-r" aria-hidden />
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-s360-violet-50 text-s360-violet">
                <UserPen className="h-5 w-5" />
              </div>
              <div className="min-w-0 pt-0.5">
                <DialogTitle className="text-xl font-semibold tracking-tight text-gray-900">
                  Edit User
                </DialogTitle>
                {/* <DialogDescription className="mt-1.5 text-sm text-gray-500 leading-relaxed">
                  Update user details and menu access permissions.
                </DialogDescription> */}
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 py-5 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Profile section */}
            <div className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Profile
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                    Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={userData?.name || ""}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className={`h-10 bg-white border-gray-200 focus-visible:ring-2 focus-visible:ring-s360-violet/20 focus-visible:ring-offset-0 ${
                      errors?.name ? "border-red-400 focus-visible:ring-red-500/20" : ""
                    }`}
                    placeholder="Enter full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={userData?.email || ""}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className={`h-10 bg-white border-gray-200 focus-visible:ring-2 focus-visible:ring-s360-violet/20 focus-visible:ring-offset-0 ${
                      errors?.email ? "border-red-400 focus-visible:ring-red-500/20" : ""
                    }`}
                    placeholder="Enter email address"
                  />
                </div>
              </div>
            </div>

            {/* Menu Access section */}
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Menu Access
              </h3>
              <p className="text-sm text-gray-500">
                Choose which sections the user can view in the app.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ALL_MENU_ITEMS.map((item) => {
                  const key = getMenuAccessKey(item);
                  const Icon = item.icon;
                  const menuAccess = userData?.company_menu_access ?? {};
                  const checked = menuAccess[key]?.view === true;
                  return (
                    <label
                      key={key}
                      htmlFor={`menu-${key}`}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                        checked
                          ? "border-s360-violet-100 bg-s360-violet-50/70"
                          : "border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <Checkbox
                        id={`menu-${key}`}
                        checked={checked}
                        onCheckedChange={(isChecked) => handleMenuAccessChange(key, !!isChecked)}
                        className="border-gray-300 data-[state=checked]:bg-s360-violet data-[state=checked]:border-s360-violet shrink-0"
                      />
                      <Icon
                        className={`w-4 h-4 shrink-0 ${checked ? "text-s360-violet" : "text-gray-500"}`}
                      />
                      <span
                        className={`text-sm font-medium ${checked ? "text-gray-900" : "text-gray-700"}`}
                      >
                        {item.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="min-w-[88px] h-9 rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-colors"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="min-w-[88px] h-9 rounded-md bg-s360-violet text-white shadow-sm hover:bg-s360-violet-600 active:bg-s360-violet-700 transition-colors disabled:opacity-60"
              disabled={isLoading}
            >
              {isLoading ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
